
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useCredentialsAuth } from "./auth/useCredentialsAuth";
import { useGoogleAuth } from "./auth/useGoogleAuth";
import { useTeamInvitation } from "./auth/useTeamInvitation";
import { useAppSumoCode } from "./auth/useAppSumoCode";

export const useAuth = () => {
  const navigate = useNavigate();
  const [localLoading, setLocalLoading] = useState(false);
  
  const {
    loading: credentialsLoading,
    email,
    setEmail,
    password,
    setPassword,
    companyName,
    setCompanyName,
    isLogin,
    setIsLogin,
    loginWithCredentials,
    signUpWithCredentials
  } = useCredentialsAuth();

  const {
    loading: googleLoading,
    handleGoogleSignIn,
    submitGoogleCompanyName,
    showGoogleCompanyPrompt,
    setShowGoogleCompanyPrompt,
    checkGoogleAuth,
    companyName: googleCompanyName,
    setCompanyName: setGoogleCompanyName
  } = useGoogleAuth();

  const {
    teamMemberId,
    loading: teamLoading,
    acceptTeamInvitation
  } = useTeamInvitation(setEmail, setIsLogin);

  const {
    appsumoCode,
    redeemAppSumoCode
  } = useAppSumoCode(setIsLogin);

  // Combined loading state - include our local loading state too
  const isLoading = localLoading || credentialsLoading || googleLoading || teamLoading;

  // Check for Google authentication on page load
  useEffect(() => {
    const checkGoogleAuthStatus = async () => {
      // Pass teamMemberId to checkGoogleAuth to handle team invitations properly
      await checkGoogleAuth(teamMemberId);
    };
    
    checkGoogleAuthStatus();
  }, [teamMemberId]);

  // Add a function to handle Google user who was invited to a team
  const handleGoogleUserTeamInvitation = async () => {
    if (!teamMemberId) return false;
    
    try {
      setLocalLoading(true);
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        throw new Error("User not found");
      }
      
      const success = await acceptTeamInvitation(user.id);
      if (success) {
        navigate("/admin");
        return true;
      }
      
      return false;
    } catch (error: any) {
      toast.error(error.message || "Failed to join team");
      return false;
    } finally {
      setLocalLoading(false);
    }
  };

  const handleAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      if (isLogin && !teamMemberId && !appsumoCode) {
        await loginWithCredentials();
      } else {
        const user = await signUpWithCredentials();
        
        if (!user) return;

        if (teamMemberId) {
          const success = await acceptTeamInvitation(user.id);
          if (success) {
            navigate("/admin");
          }
        } else if (appsumoCode) {
          const success = await redeemAppSumoCode(user.id);
          if (success) {
            navigate("/admin");
          }
        }
      }
    } catch (error: any) {
      toast.error(error.message);
    }
  };

  return {
    loading: isLoading,
    email,
    setEmail,
    password,
    setPassword,
    companyName,
    setCompanyName,
    isLogin,
    setIsLogin,
    handleAuth,
    handleGoogleSignIn,
    teamMemberId,
    appsumoCode,
    showGoogleCompanyPrompt,
    submitGoogleCompanyName,
    handleGoogleUserTeamInvitation,
    googleCompanyName,
    setGoogleCompanyName
  };
};
